import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useLocation, ScrollRestoration } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, homeForRole } from "./lib/auth";
import { UnsavedChangesProvider, fromLocation } from "./lib/nav";
import PublicLayout from "./components/PublicLayout";
import { Spinner } from "./components/states";

import Live from "./pages/Live";
import Library from "./pages/Library";
import Picks from "./pages/Picks";
import Artists from "./pages/Artists";
import Events from "./pages/Events";
import Shop from "./pages/Shop";
import Slots from "./pages/Slots";
import RecordingDetail from "./pages/RecordingDetail";
import Highlight from "./pages/Highlight";
import Membership from "./pages/Membership";
import Guide from "./pages/Guide";
import PayReturn from "./pages/PayReturn";
import OrganizerPortal from "./pages/OrganizerPortal";
import DjConsole from "./pages/DjConsole";
import Login from "./pages/Login";
import Register from "./pages/Register";
import { ForgotPassword, ResetPassword, VerifyEmail } from "./pages/AccountRecovery";
import AuthCallback from "./pages/AuthCallback";
import Notifications from "./pages/Notifications";
import UserDashboard from "./pages/UserDashboard";
import DjPortal from "./pages/DjPortal";
import AdminConsole from "./pages/AdminConsole";
import SuperConsole from "./pages/SuperConsole";

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen bg-[#0A0A0C]"><Spinner /></div>;
  if (!user) return <Navigate to="/login" state={{ from: fromLocation(loc) }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeForRole(user.role)} replace />;
  return children;
}

function Public({ children }) {
  return <PublicLayout>{children}</PublicLayout>;
}

function AppRoutes() {
  const location = useLocation();
  // Google OAuth callback — process session_id before anything else
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Public><Live /></Public>} />
      <Route path="/library" element={<Public><Library /></Public>} />
      <Route path="/picks" element={<Public><Picks /></Public>} />
      <Route path="/artists" element={<Public><Artists /></Public>} />
      <Route path="/events" element={<Public><Events /></Public>} />
      <Route path="/shop" element={<Public><Shop /></Public>} />
      <Route path="/slots" element={<Public><Slots /></Public>} />
      <Route path="/recording/:id" element={<Public><RecordingDetail /></Public>} />
      <Route path="/h/:id" element={<Public><Highlight /></Public>} />
      <Route path="/membership" element={<Public><Membership /></Public>} />
      <Route path="/guide" element={<Public><Guide /></Public>} />
      <Route path="/pay/return" element={<Public><PayReturn /></Public>} />
      <Route path="/organizer" element={<Protected roles={["ORGANIZER", "ADMIN", "SUPER_ADMIN"]}><OrganizerPortal /></Protected>} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/dashboard" element={<Protected roles={["USER", "ADMIN", "SUPER_ADMIN", "DJ", "ORGANIZER"]}><UserDashboard /></Protected>} />
      <Route path="/dj" element={<Protected roles={["DJ", "ADMIN", "SUPER_ADMIN"]}><DjConsole /></Protected>} />
      <Route path="/dj/legacy" element={<Protected roles={["DJ", "ADMIN", "SUPER_ADMIN"]}><DjPortal /></Protected>} />
      <Route path="/console" element={<Protected roles={["ADMIN", "SUPER_ADMIN"]}><AdminConsole /></Protected>} />
      <Route path="/console/super" element={<Protected roles={["SUPER_ADMIN"]}><SuperConsole /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Root() {
  return (
    <AuthProvider>
      <UnsavedChangesProvider>
        <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#141419", border: "1px solid #27272A", color: "#F4F4F5" } }} />
        <ScrollRestoration getKey={(loc) => loc.pathname + loc.search} />
        <AppRoutes />
      </UnsavedChangesProvider>
    </AuthProvider>
  );
}

// Data router: enables useBlocker (unsaved-changes guard) and ScrollRestoration with full browser Back/Forward support.
const router = createBrowserRouter([{ path: "*", element: <Root /> }]);

export default function App() {
  return <RouterProvider router={router} />;
}
